// Copyright (c) 2026 Michael F. Kamprath
//
// voice-set.js -- Voice set selection and URL resolution.
//
// A voice set is a named collection of audio file URL templates for one
// language: number recordings (per numeral script) plus shared prompt
// recordings (counting prompts, praise, interjections, failure lines).
//
// Each language defines one or more voice sets in voice-sets.json.  At
// the start of each scene the game manager calls pickVoiceSetForLocale
// once and uses the returned voice set for the entire round.
//
// Fallback: if the chosen voice set does not define the template needed
// to resolve a particular file (e.g. the legacy "child" set only ships
// number recordings, not prompts), the resolver falls back to the first
// voice set in the locale's list.  This mirrors the legacy iOS app's
// behavior of layering child-voice numbers on top of a shared adult set.
//
// URL templates use %d for integer substitution (numbers) and %s for
// the file basename (prompts).  Resolved URLs always start with '/'.

let cached = null;

/**
 * Load voice-sets.json (cached after the first call).
 *
 * @param {string} [url]  Override URL for tests.
 * @returns {Promise<object>}
 */
export async function loadVoiceSets(url = '/voice-sets.json') {
  if (cached) return cached;
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`failed to load voice-sets.json: ${res.status}`);
  }
  cached = await res.json();
  return cached;
}

/**
 * Return the voiceSets array for a locale (empty array if locale absent).
 *
 * @param {object} data    Parsed voice-sets.json.
 * @param {string} locale
 * @returns {Array<object>}
 */
export function getVoiceSetsForLocale(data, locale) {
  const entry = data?.[locale];
  return Array.isArray(entry?.voiceSets) ? entry.voiceSets : [];
}

/**
 * Randomly pick one voice set for a scene.  Returns null if the locale
 * has no voice sets defined.
 *
 * @param {object}   data    Parsed voice-sets.json.
 * @param {string}   locale
 * @param {() => number} [rng]  Injection point for tests; defaults to Math.random.
 * @returns {object|null}
 */
export function pickVoiceSetForLocale(data, locale, rng = Math.random) {
  const sets = getVoiceSetsForLocale(data, locale);
  if (sets.length === 0) return null;
  const idx = Math.floor(rng() * sets.length);
  // Clamp to last index in the unlikely case rng() returns exactly 1.
  return sets[Math.min(idx, sets.length - 1)];
}

/**
 * Resolve a number audio URL.
 *
 * Looks for `voiceSet.numbers[numeralScript]` (a "%d" template); if the
 * active voice set doesn't define one, falls back to the first voice
 * set in the locale.  Returns null if no voice set has a template for
 * the requested script.
 *
 * @param {object} data
 * @param {string} locale
 * @param {object} voiceSet
 * @param {number} n
 * @param {string} [numeralScript]
 * @returns {string|null}
 */
export function resolveNumber(data, locale, voiceSet, n, numeralScript = 'arabic') {
  // A null voiceSet means upstream never picked one — don't silently
  // fall back, that hides bugs in the game manager.
  if (!voiceSet) return null;
  const direct = numberUrlFromVoiceSet(voiceSet, n, numeralScript);
  if (direct) return ensureLeadingSlash(direct);
  const sets = getVoiceSetsForLocale(data, locale);
  if (sets.length > 0 && sets[0] !== voiceSet) {
    const fallback = numberUrlFromVoiceSet(sets[0], n, numeralScript);
    if (fallback) return ensureLeadingSlash(fallback);
  }
  return null;
}

/**
 * Resolve a prompt audio URL by basename (e.g. "howmany_sheep",
 * "goodcounting", "letscount1").
 *
 * Looks for `voiceSet.prompts` (a "%s" template); if the active voice
 * set doesn't define one, falls back to the first voice set in the
 * locale.  Returns null if no voice set has a prompts template.
 *
 * @param {object} data
 * @param {string} locale
 * @param {object} voiceSet
 * @param {string} basename
 * @returns {string|null}
 */
export function resolvePrompt(data, locale, voiceSet, basename) {
  if (!voiceSet) return null;
  if (!basename) return null;
  const direct = promptUrlFromVoiceSet(voiceSet, basename);
  if (direct) return ensureLeadingSlash(direct);
  const sets = getVoiceSetsForLocale(data, locale);
  if (sets.length > 0 && sets[0] !== voiceSet) {
    const fallback = promptUrlFromVoiceSet(sets[0], basename);
    if (fallback) return ensureLeadingSlash(fallback);
  }
  return null;
}

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------

function numberUrlFromVoiceSet(voiceSet, n, numeralScript) {
  const tpl = voiceSet?.numbers?.[numeralScript];
  if (typeof tpl !== 'string') return null;
  return tpl.replace('%d', String(n));
}

function promptUrlFromVoiceSet(voiceSet, basename) {
  const tpl = voiceSet?.prompts;
  if (typeof tpl !== 'string') return null;
  return tpl.replace('%s', basename);
}

function ensureLeadingSlash(p) {
  return p.startsWith('/') ? p : '/' + p;
}

/**
 * Test hook: drop the cached voice-sets.json so the next loadVoiceSets
 * call re-fetches.
 */
export function __resetForTests() {
  cached = null;
}
